// core/AiInsightWidget.tsx
import { useState } from "react";

interface Insight {
    type: "causation" | "anomaly" | "warning" | "info";
    title: string;
    detail: string;
}

interface AnalysisReport {
    predictive_power_score: number;
    tldr: string;
    key_insights: Insight[];
    suggested_actions: string[];
}

interface Props {
    report: AnalysisReport;
}

const INSIGHT_COLORS: Record<string, { bg: string; border: string; icon: string }> = {
    causation: { bg: "var(--accent-glow)", border: "var(--accent-primary)", icon: "🔗" },
    anomaly: { bg: "var(--accent-danger-subtle)", border: "var(--accent-danger)", icon: "⚡" },
    warning: { bg: "var(--accent-warning-subtle)", border: "var(--accent-warning)", icon: "⚠️" },
    info: { bg: "var(--accent-success-subtle)", border: "var(--accent-success)", icon: "ℹ️" },
};

function GaugeArc({ score }: { score: number }) {
    const clamp = Math.max(0, Math.min(100, score));
    const radius = 52;
    const cx = 70;
    const cy = 70;
    const startAngle = -200;
    const sweep = 220;
    const endAngle = startAngle + (sweep * clamp) / 100;

    const toRad = (d: number) => (d * Math.PI) / 180;
    const pt = (a: number) => ({
        x: cx + radius * Math.cos(toRad(a)),
        y: cy + radius * Math.sin(toRad(a)),
    });
    const start = pt(startAngle);
    const end = pt(endAngle);
    const trackEnd = pt(startAngle + sweep);
    const large = (sweep * clamp) / 100 > 180 ? 1 : 0;

    const colour =
        clamp >= 70 ? "var(--accent-success)" : clamp >= 40 ? "var(--accent-warning)" : "var(--accent-danger)";

    return (
        <svg width={140} height={110} viewBox="0 0 140 110" style={{ overflow: "visible" }}>
            {/* Track */}
            <path
                d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 1 1 ${trackEnd.x} ${trackEnd.y}`}
                fill="none"
                stroke="var(--border-color)"
                strokeWidth={10}
                strokeLinecap="round"
            />
            {/* Fill arc */}
            {clamp > 0 && (
                <path
                    d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 1 ${end.x} ${end.y}`}
                    fill="none"
                    stroke={colour}
                    strokeWidth={10}
                    strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 4px ${colour})` }}
                />
            )}
            {/* Score text */}
            <text x={cx} y={cy + 6} textAnchor="middle" fontSize={22} fontWeight={700} fill="var(--text-primary)">
                {clamp}
            </text>
            <text x={cx} y={cy + 22} textAnchor="middle" fontSize={9} fill="var(--text-secondary)">
                PREDICTIVE POWER
            </text>
        </svg>
    );
}

export function AiInsightWidget({ report }: Props) {
    const [expanded, setExpanded] = useState<number | null>(null);

    return (
        <div
            style={{
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--radius-md, 12px)",
                overflow: "hidden",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                color: "var(--text-primary)"
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: "0.6rem 1rem",
                    borderBottom: "1px solid var(--border-color)",
                    background: "var(--accent-glow)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "move",
                }}
                className="widget-header"
            >
                <span>🤖</span>
                <span style={{ color: "var(--text-primary)" }}>AI Insights</span>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "0.75rem 1rem" }}>
                {/* Gauge + TLDR */}
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
                    <GaugeArc score={report.predictive_power_score} />
                    <p
                        style={{
                            margin: 0,
                            fontSize: "0.8rem",
                            color: "var(--text-secondary)",
                            lineHeight: 1.5,
                            fontStyle: "italic",
                        }}
                    >
                        {report.tldr}
                    </p>
                </div>

                {/* Insight badges */}
                {report.key_insights.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.75rem" }}>
                        {report.key_insights.map((insight, i) => {
                            const style = INSIGHT_COLORS[insight.type] ?? INSIGHT_COLORS.info;
                            const isOpen = expanded === i;
                            return (
                                <div
                                    key={i}
                                    onClick={() => setExpanded(isOpen ? null : i)}
                                    style={{
                                        background: style.bg,
                                        border: `1px solid ${style.border}`,
                                        borderRadius: "8px",
                                        padding: "0.45rem 0.7rem",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                        <span style={{ fontSize: "0.85rem" }}>{style.icon}</span>
                                        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-primary)" }}>
                                            {insight.title}
                                        </span>
                                        <span style={{ marginLeft: "auto", opacity: 0.5, fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                                            {isOpen ? "▲" : "▼"}
                                        </span>
                                    </div>
                                    {isOpen && (
                                        <p
                                            style={{
                                                margin: "0.35rem 0 0 1.3rem",
                                                fontSize: "0.75rem",
                                                color: "var(--text-secondary)",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {insight.detail}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Suggested actions */}
                {report.suggested_actions.length > 0 && (
                    <div>
                        <p style={{ margin: "0 0 0.3rem 0", fontSize: "0.72rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Suggested Actions
                        </p>
                        <ul style={{ margin: 0, padding: "0 0 0 1.1rem" }}>
                            {report.suggested_actions.map((a, i) => (
                                <li key={i} style={{ fontSize: "0.76rem", color: "var(--text-secondary)", marginBottom: "0.2rem" }}>
                                    {a}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}


